import React, { createContext, useContext, useState, useCallback } from 'react';
const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    const notification = { id, message, type };
    
    setNotifications(prev => [...prev, notification]);
    
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
    
    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const success = useCallback((message, duration) => {
    return addNotification(message, 'success', duration);
  }, [addNotification]);

  const error = useCallback((message, duration) => {
    return addNotification(message, 'error', duration);
  }, [addNotification]);

  const info = useCallback((message, duration) => {
    return addNotification(message, 'info', duration);
  }, [addNotification]);

  const warning = useCallback((message, duration) => {
    return addNotification(message, 'warning', duration);
  }, [addNotification]);

  const confirm = useCallback((message, onConfirm, onCancel) => {
    const id = Date.now() + Math.random();
    const notification = {
      id,
      message,
      type: 'confirm',
      onConfirm: () => {
        removeNotification(id);
        if (onConfirm) onConfirm();
      },
      onCancel: () => {
        removeNotification(id);
        if (onCancel) onCancel();
      }
    };
    
    setNotifications(prev => [...prev, notification]);
    return id;
  }, [removeNotification]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    success,
    error,
    info,
    warning,
    confirm
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="notification-container">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`notification notification-${notification.type}`}
          >
            <div className="notification-content">
              {notification.type === 'success' && <span className="notification-icon">✓</span>}
              {notification.type === 'error' && <span className="notification-icon">✕</span>}
              {notification.type === 'warning' && <span className="notification-icon">⚠</span>}
              {notification.type === 'info' && <span className="notification-icon">ℹ</span>}
              <span className="notification-message">{notification.message}</span>
            </div>
            {notification.type === 'confirm' ? (
              <div className="notification-actions">
                <button
                  className="notification-btn notification-btn-confirm"
                  onClick={notification.onConfirm}
                >
                  Confirm
                </button>
                <button
                  className="notification-btn notification-btn-cancel"
                  onClick={notification.onCancel}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                className="notification-close"
                onClick={() => removeNotification(notification.id)}
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};


